import AdminPanelLayout from "@/layouts/AdminPanelLayout";
import { createApprovalGroupDetailsBreadcrumb } from "@/queries/approval-groups";
import { createTableDetailsBreadcrumb } from "@/queries/data-center/data-center.api";
import { createEntityDetailsBreadcrumb } from "@/queries/digital-files/entities.api";
import { createFlowDetailsBreadcrumb } from "@/queries/flows/flows.api";
import {
  createFormalityRequestBreadcrumb,
  createGetFormDetailBreadcrumb,
} from "@/queries/formaly-request";
import { preLoadProfileInfo } from "@/queries/me";
import { createProcessDetailsBreadcrumb } from "@/queries/process/process.api";
import { createRoleDetailsBreadcrumb } from "@/queries/roles";
import { createExternalUserDetailsBreadcrumb } from "@/queries/users/external-users.api";
import { createUserDetailsBreadcrumb } from "@/queries/users/users.api";
import type { BreadcrumbHandle } from "@/types/router-breadcrumb";
import { Permissions } from "@/utils/permissions";
import type { QueryClient } from "@tanstack/react-query";
import { createBrowserRouter, redirect, type UIMatch } from "react-router";
import Page403 from "./403";
import Calendar from "./calendar";
import CentroDeDatos from "./data-center";
import { DetalleTabla } from "./data-center/components/DetalleTabla";
import { CFDetailTable } from "./data-center/functional-core/CFDetailTable";
import DigitalFilesPage from "./digital-files";
import { EntityPage } from "./digital-files/entity";
import { EntityRegistryPage } from "./digital-files/entity-registry";
import FaqPage from "./faq";
import EditFlowPage from "./flows/edit";
import FlowExecution from "./flows/execution";
import Home from "./home";
import LegalPage from "./legal";
import { PortalPage } from "./portal";
import { FaqsPage } from "./portal/faqs";
import NotificationsPage from "./portal/notifications";
import SuspensionsPage from "./portal/suspensions/SuspensionsPage";
import { ProcedureDetailPage } from "./procedures";
import FormCorrection from "./procedures/components/form/form-correction";
import { FormDetail } from "./procedures/components/form/form-detail";
import { FormInterns } from "./procedures/components/form/form-interns";
import { ProcedureFormsList } from "./procedures/components/form/form-list";
import { PaymentDetail } from "./procedures/components/payment/PaymentDetail";
import SignatureDetail from "./procedures/components/signature/signature-detail";
import ProcessPage from "./procesos";
import { DetalleProceso } from "./procesos/details";
import { TagDetailsPage } from "./procesos/tag-details";
import Profile from "./profile";
import RoleGuard from "./role-guard";
import Roles from "./roles";
import RoleDetails from "./roles/details";
import NewRole from "./roles/new";
import TemplatesPage from "./templates";
import { DetalleEmailTemplate } from "./templates/components/DetalleEmailTemplate";
import TemplateEditor from "./templates/components/DocumentEditor";
import UserDetails from "./users/details";
import ExternalUserDetails from "./users/external/details";
import NewExternalUser from "./users/external/new";
import { EditApprovalGroup } from "./users/groups/edit";
import { CreateApprovalGroup } from "./users/groups/new";
import NewUser from "./users/new";
import { UsersAndGroups } from "./users/UsersAndGroups";
import { createRegistryDetailsBreadcrumb } from "@/queries/digital-files/registries.api";
import { RegistryDetailPage } from "./digital-files/entity-registry/detail-page";
import InfoPage from "./info";
import { getEnv } from "@/utils/runtime-config";

export const createAppRouter = (queryClient: QueryClient) => {
  const shouldShowInfoPage = getEnv("SHOW_INFO_PAGE", "true") === "true";

  return createBrowserRouter([
    {
      Component: AdminPanelLayout,
      path: "/",
      children: [
        {
          index: true,
          Component: () => (
            <RoleGuard permissions={[]}>
              <Home />
            </RoleGuard>
          ),
          handle: {
            breadcrumb: () => [
              {
                label: "Inicio",
              },
            ],
          },
        },
        {
          path: "perfil",
          element: <Profile />,
          loader: async () => await preLoadProfileInfo(queryClient)(),
          handle: {
            breadcrumb: () => [
              {
                label: "Mi Perfil",
              },
            ],
          } as BreadcrumbHandle,
        },
        {
          path: "procesos",
          children: [
            {
              index: true,
              element: (
                <RoleGuard permissions={[Permissions.processes.read]}>
                  <ProcessPage />
                </RoleGuard>
              ),
              handle: {
                breadcrumb: () => [
                  {
                    label: "Procesos",
                  },
                ],
              } as BreadcrumbHandle,
            },
            {
              path: ":id/flujo/:flowId",
              loader: async ({ params }) => {
                const process = await createProcessDetailsBreadcrumb(
                  queryClient,
                  params.id!,
                )();
                const flow = await createFlowDetailsBreadcrumb(
                  queryClient,
                  params.flowId!,
                )();
                return {
                  data: {
                    name: `${process?.data?.name || ""} -seperator-breadcrum-#321309- ${flow?.data?.name || ""}`,
                  },
                };
              },
              handle: {
                breadcrumb: (match) => {
                  const params = match.params;
                  const [processName, flowName] =
                    match?.data?.data?.name?.split(
                      "-seperator-breadcrum-#321309-",
                    ) || ["", ""];
                  return [
                    {
                      label: "Procesos",
                      path: "/procesos",
                    },
                    {
                      label: processName ?? "Detalle",
                      path: `/procesos/${params.id}`,
                    },
                    {
                      label: flowName ?? "Flujo",
                    },
                  ];
                },
              } as BreadcrumbHandle,
              element: (
                <RoleGuard
                  permissions={[
                    Permissions.flows.read,
                    Permissions.flows.update,
                  ]}
                >
                  <EditFlowPage />
                </RoleGuard>
              ),
            },
            {
              path: ":id/flujo/:flowId/ejecuciones",
              loader: async ({ params }) => {
                const process = await createProcessDetailsBreadcrumb(
                  queryClient,
                  params.id!,
                )();
                const flow = await createFlowDetailsBreadcrumb(
                  queryClient,
                  params.flowId!,
                )();
                return {
                  data: {
                    name: `${process?.data?.name || ""} -seperator-breadcrum-#321309- ${flow?.data?.name || ""}`,
                  },
                };
              },
              handle: {
                breadcrumb: (match) => {
                  const params = match.params;
                  const [processName, flowName] =
                    match?.data?.data?.name?.split(
                      "-seperator-breadcrum-#321309-",
                    ) || ["", ""];
                  return [
                    {
                      label: "Procesos",
                      path: "/procesos",
                    },
                    {
                      label: processName ?? "Detalle",
                      path: `/procesos/${params.id}`,
                    },
                    {
                      label: flowName ?? "Flujo",
                    },
                  ];
                },
              } as BreadcrumbHandle,
              element: (
                <RoleGuard
                  permissions={[
                    Permissions.flows.read,
                    Permissions.flows.update,
                  ]}
                >
                  <FlowExecution />
                </RoleGuard>
              ),
            },
            {
              path: ":id",
              element: (
                <RoleGuard permissions={[Permissions.processes.read]}>
                  <DetalleProceso />
                </RoleGuard>
              ),
              loader: async ({ params, request }) => {
                const url = new URL(request.url);
                const type = url.searchParams.get("type");
                return await createProcessDetailsBreadcrumb(
                  queryClient,
                  params.id!,
                  type,
                )();
              },
              handle: {
                breadcrumb: (match) => [
                  {
                    label: "Procesos",
                    path: "/procesos",
                  },
                  {
                    label: match?.data?.data?.name ?? "Detalle",
                  },
                ],
              } as BreadcrumbHandle,
            },
            {
              path: "etiquetas/:id",
              element: (
                <RoleGuard permissions={[Permissions.tags.read]}>
                  <TagDetailsPage />
                </RoleGuard>
              ),
              loader: async () => {
                // For now, return a simple breadcrumb. We can enhance this later
                return {
                  data: {
                    name: "Detalle de Etiqueta",
                  },
                };
              },
              handle: {
                breadcrumb: (match) => [
                  {
                    label: "Procesos",
                    path: "/procesos",
                  },
                  {
                    label: "Etiquetas",
                    path: "/procesos",
                  },
                  {
                    label: match?.data?.data?.name ?? "Detalle de Etiqueta",
                  },
                ],
              } as BreadcrumbHandle,
            },
          ],
        },
        {
          path: "centro-de-datos",
          children: [
            {
              index: true,
              element: (
                <RoleGuard permissions={[Permissions["data-center"].read]}>
                  <CentroDeDatos />
                </RoleGuard>
              ),
              handle: {
                breadcrumb: () => [
                  {
                    label: "Centro de datos",
                  },
                ],
              } as BreadcrumbHandle,
            },
            {
              path: ":id",
              element: (
                <RoleGuard
                  permissions={[
                    Permissions["data-center"]["read-registry"],
                    Permissions["data-center"].read,
                  ]}
                >
                  <DetalleTabla />
                </RoleGuard>
              ),
              loader: async ({ params }) =>
                await createTableDetailsBreadcrumb(queryClient, params.id!)(),
              handle: {
                breadcrumb: (match) => {
                  const data: {
                    label: string;
                    path?: string;
                  }[] = [
                    {
                      label: "Centro de datos",
                      path: "/centro-de-datos",
                    },
                  ];
                  if (match?.data?.data?.name) {
                    data.push({
                      label: match.data.data.name || "Detalles de la tabla",
                    });
                  }
                  return data;
                },
              } as BreadcrumbHandle,
            },
            {
              path: "core-funcional/:id",
              element: (
                <RoleGuard
                  permissions={[
                    Permissions["data-center"]["read-registry"],
                    Permissions["data-center"].read,
                  ]}
                >
                  <CFDetailTable />
                </RoleGuard>
              ),
              handle: {
                breadcrumb: (match) => {
                  const data: {
                    label: string;
                    path?: string;
                  }[] = [
                    {
                      label: "Centro de datos",
                      path: "/centro-de-datos?core=true",
                    },
                  ];
                  if (match?.params?.id) {
                    data.push({
                      label: match.params.id || "Detalles de la tabla",
                    });
                  }
                  return data;
                },
              } as BreadcrumbHandle,
            },
          ],
        },
        {
          path: "plantillas",
          children: [
            {
              index: true,
              element: (
                <RoleGuard
                  canHaveSomePermissions
                  permissions={[
                    Permissions["email-templates"].read,
                    Permissions["document-templates"].read,
                  ]} //TODO cambiar a templates
                >
                  <TemplatesPage />
                </RoleGuard>
              ),
              handle: {
                breadcrumb: () => [{ label: "Plantillas" }],
              } as BreadcrumbHandle,
            },
            {
              path: "documentos/:id",
              element: (
                <RoleGuard
                  permissions={[Permissions["document-templates"].update]} //TODO cambiar a templates.update
                >
                  <TemplateEditor />
                </RoleGuard>
              ),
              handle: {
                breadcrumb: () => [
                  { label: "Plantillas", path: "/plantillas" },
                  { label: "Editor" },
                ],
              } as BreadcrumbHandle,
            },
            {
              path: "correos/:id",
              element: (
                <RoleGuard permissions={[Permissions["email-templates"].read]}>
                  <DetalleEmailTemplate />
                </RoleGuard>
              ),
              handle: {
                breadcrumb: () => [
                  {
                    label: "Plantillas",
                    path: "/plantillas",
                  },
                  {
                    label: "Detalle de plantilla",
                  },
                ],
              } as BreadcrumbHandle,
            },
          ],
        },
        {
          path: "aplicaciones",
          Component: () => <div>Aplicaciones</div>,
          handle: {
            breadcrumb: () => [
              {
                label: "Aplicaciones",
              },
            ],
          } as BreadcrumbHandle,
        },
        {
          path: "roles",
          children: [
            {
              index: true,
              element: (
                <RoleGuard permissions={[Permissions.roles.read]}>
                  <Roles />
                </RoleGuard>
              ),
              handle: {
                breadcrumb: () => [
                  {
                    label: "Roles",
                  },
                ],
              } as BreadcrumbHandle,
            },
            {
              path: "new",
              element: (
                <RoleGuard
                  permissions={[
                    Permissions.roles.create,
                    Permissions.roles.read,
                    Permissions.roles["assign-permissions"],
                  ]}
                >
                  <NewRole />
                </RoleGuard>
              ),
              handle: {
                breadcrumb: () => [
                  {
                    label: "Roles",
                    path: "/roles",
                  },
                  {
                    label: "Crear rol",
                  },
                ],
              } as BreadcrumbHandle,
            },
            {
              path: ":id",
              element: (
                <RoleGuard
                  permissions={[
                    Permissions.roles.update,
                    Permissions.roles.read,
                  ]}
                >
                  <RoleDetails />
                </RoleGuard>
              ),
              loader: async ({ params }) =>
                await createRoleDetailsBreadcrumb(queryClient, params.id!)(),
              handle: {
                breadcrumb: (match) => {
                  const data: {
                    label: string;
                    path?: string;
                  }[] = [
                    {
                      label: "Roles",
                      path: "/roles",
                    },
                  ];
                  if (match?.data?.data?.name) {
                    data.push({
                      label: match.data.data.name || "Detalles del rol",
                    });
                  }
                  return data;
                },
              } as BreadcrumbHandle,
            },
          ],
        },
        {
          path: "usuarios",
          children: [
            {
              index: true,
              Component: () => (
                <RoleGuard
                  permissions={[
                    Permissions.users.read,
                    Permissions["approval-groups"].read,
                  ]}
                  canHaveSomePermissions
                >
                  <UsersAndGroups />
                </RoleGuard>
              ),
              handle: {
                breadcrumb: () => [
                  {
                    label: "Usuarios",
                  },
                ],
              } as BreadcrumbHandle,
            },
            {
              path: "grupos/new",
              element: (
                <RoleGuard
                  permissions={[Permissions["approval-groups"].create]}
                >
                  <CreateApprovalGroup />
                </RoleGuard>
              ),
              handle: {
                breadcrumb: () => [
                  {
                    label: "Grupos",
                    path: "/usuarios?tab=groups",
                  },
                  {
                    label: "Crear grupo",
                  },
                ],
              },
            },
            {
              path: "grupos/:id",
              element: (
                <RoleGuard
                  permissions={[Permissions["approval-groups"].update]}
                >
                  <EditApprovalGroup />
                </RoleGuard>
              ),
              loader: async ({ params }) => {
                return await createApprovalGroupDetailsBreadcrumb(
                  queryClient,
                  params.id!,
                )();
              },
              handle: {
                breadcrumb: (match) => [
                  {
                    label: "Grupos",
                    path: "/usuarios?tab=groups",
                  },
                  {
                    label: match?.data?.data?.name ?? "Detalle del Grupo",
                  },
                ],
              } as BreadcrumbHandle,
            },
            {
              path: "new",
              element: (
                <RoleGuard permissions={[Permissions.users.create]}>
                  <NewUser />
                </RoleGuard>
              ),
              handle: {
                breadcrumb: () => [
                  {
                    label: "Usuarios",
                    path: "/usuarios",
                  },
                  {
                    label: "Crear usuario interno",
                  },
                ],
              },
            },
            {
              path: "externos/new",
              element: (
                <RoleGuard permissions={[Permissions.users.create]}>
                  <NewExternalUser />
                </RoleGuard>
              ),
              handle: {
                breadcrumb: () => [
                  {
                    label: "Usuarios",
                    path: "/usuarios?user-tab=externos",
                  },
                  {
                    label: "Crear usuario externo",
                  },
                ],
              },
            },
            {
              path: "externos/:id",
              element: (
                <RoleGuard permissions={[Permissions.users.update]}>
                  <ExternalUserDetails />
                </RoleGuard>
              ),
              loader: async ({ params }) =>
                await createExternalUserDetailsBreadcrumb(
                  queryClient,
                  params.id!,
                )(),
              handle: {
                breadcrumb: (
                  match: UIMatch<{ data: { name: string }; error: string }>,
                ) => {
                  const data: {
                    label: string;
                    path?: string;
                  }[] = [
                    {
                      label: "Usuarios",
                      path: "/usuarios?user-tab=externos",
                    },
                    {
                      label: match?.data?.data?.name || "Detalles del usuario",
                    },
                  ];
                  return data;
                },
              },
            },
            {
              path: ":id",
              element: (
                <RoleGuard permissions={[Permissions.users.update]}>
                  <UserDetails />
                </RoleGuard>
              ),
              loader: async ({ params }) =>
                await createUserDetailsBreadcrumb(queryClient, params.id!)(),
              handle: {
                breadcrumb: (match) => {
                  const data: {
                    label: string;
                    path?: string;
                  }[] = [
                    {
                      label: "Usuarios",
                      path: "/usuarios",
                    },
                    {
                      label: match?.data?.data?.name || "Detalles del usuario",
                    },
                  ];
                  return data;
                },
              } as BreadcrumbHandle,
            },
          ],
        },
        {
          path: "calendario",
          children: [
            {
              index: true,
              element: (
                <RoleGuard permissions={[Permissions.calendar.read]}>
                  <Calendar />
                </RoleGuard>
              ),
              handle: {
                breadcrumb: () => [
                  {
                    label: "Calendario",
                  },
                ],
              } as BreadcrumbHandle,
            },
          ],
        },
        {
          path: "portal",
          children: [
            {
              index: true,
              element: (
                <RoleGuard
                  permissions={[
                    Permissions.faqs.read,
                    Permissions.announcements.read,
                  ]}
                  canHaveSomePermissions
                >
                  <PortalPage />
                </RoleGuard>
              ),
              handle: {
                breadcrumb: () => [{ label: "Portal" }],
              } as BreadcrumbHandle,
            },
            {
              path: "preguntas-frecuentes",
              element: (
                <RoleGuard permissions={[Permissions.faqs.read]}>
                  <FaqsPage />
                </RoleGuard>
              ),
              handle: {
                breadcrumb: () => [
                  { label: "Portal", path: "/portal" },
                  { label: "Preguntas frecuentes" },
                ],
              },
            },
            {
              path: "notifications",
              element: (
                <RoleGuard permissions={[Permissions.announcements.read]}>
                  <NotificationsPage />
                </RoleGuard>
              ),
              handle: {
                breadcrumb: () => [
                  { label: "Portal", path: "/portal" },
                  { label: "Notificaciones Generales" },
                ],
              },
            },
            {
              path: "plazos-de-suspension",
              element: (
                <RoleGuard
                  permissions={[
                    Permissions["node-deadline-suspensions-config"].read,
                    Permissions["tags"].read,
                  ]}
                >
                  <SuspensionsPage />
                </RoleGuard>
              ),
              handle: {
                breadcrumb: () => [
                  { label: "Portal", path: "/portal" },
                  { label: "Plazos de Suspensión" },
                ],
              },
            },
          ],
        },
        {
          path: "finanzas",
          Component: () => <div className="text-body">Finanzas</div>,
          handle: {
            breadcrumb: () => [
              {
                label: "Finanzas",
              },
            ],
          } as BreadcrumbHandle,
        },
        {
          path: "reguladores",
          Component: () => <div className="text-body">Reguladores</div>,
          handle: {
            breadcrumb: () => [
              {
                label: "Reguladores",
              },
            ],
          } as BreadcrumbHandle,
        },
        {
          path: "legal-info",
          element: (
            <RoleGuard permissions={[Permissions["privacy-policies"].view]}>
              <LegalPage />
            </RoleGuard>
          ),
          handle: {
            breadcrumb: () => [
              {
                label: "Información Legal",
              },
            ],
          } as BreadcrumbHandle,
        },
        {
          path: "faq",
          element: (
            <RoleGuard permissions={[Permissions.faqs.read]}>
              <FaqPage />
            </RoleGuard>
          ),
          handle: {
            breadcrumb: () => [
              {
                label: "Preguntas frecuentes",
              },
            ],
          } as BreadcrumbHandle,
        },
        {
          path: "tramite/:procedureId",
          element: (
            <RoleGuard permissions={[Permissions.formality_requests.read]}>
              <ProcedureDetailPage />
            </RoleGuard>
          ),
          children: [
            {
              index: true,
              element: <ProcedureFormsList />,
              loader: async ({ params }) =>
                await createFormalityRequestBreadcrumb(
                  queryClient,
                  params.procedureId!,
                )(),
              handle: {
                breadcrumb: (match) => {
                  const data: {
                    label: string;
                    path?: string;
                  }[] = [
                    {
                      label: "Mis Trámites",
                      path: "/",
                    },
                    {
                      label: match?.data?.data?.name || "Detalle",
                    },
                  ];
                  return data;
                },
              } as BreadcrumbHandle,
            },
            {
              path: "correccion/:formId",
              element: (
                <RoleGuard permissions={[Permissions.widgets.submissions]}>
                  <FormCorrection />
                </RoleGuard>
              ),
              loader: async ({ params }) =>
                await createGetFormDetailBreadcrumb(
                  queryClient,
                  params.formId!,
                  params.procedureId!,
                )(),
              handle: {
                breadcrumb: (match) => {
                  const data: {
                    label: string;
                    path?: string;
                  }[] = [
                    {
                      label: "Mis Trámites",
                      path: "/",
                    },
                    {
                      label: "Detalle",
                      path: `/tramite/${match.params.procedureId}`,
                    },
                    {
                      label: match?.data?.data?.name || "Nombre del Formulario",
                    },
                  ];
                  return data;
                },
              } as BreadcrumbHandle,
            },
            {
              path: "firma/:singRequestId",
              element: (
                <RoleGuard permissions={[Permissions.widgets.submissions]}>
                  <SignatureDetail />
                </RoleGuard>
              ),
              handle: {
                breadcrumb: (match) => {
                  const data: { label: string; path?: string }[] = [
                    { label: "Mis Trámites", path: "/" },
                    {
                      label: "Detalle",
                      path: `/tramite/${match.params.procedureId}`,
                    },
                    { label: "Firma de documentos" },
                  ];
                  return data;
                },
              } as BreadcrumbHandle,
            },
            {
              path: "pago/:paymentRequestId",
              element: (
                <RoleGuard permissions={[Permissions.widgets.submissions]}>
                  <PaymentDetail />
                </RoleGuard>
              ),
              handle: {
                breadcrumb: (match) => {
                  const data: { label: string; path?: string }[] = [
                    { label: "Mis Trámites", path: "/" },
                    {
                      label: "Detalle",
                      path: `/tramite/${match.params.procedureId}`,
                    },
                    { label: "Detalle de pago" },
                  ];
                  return data;
                },
              } as BreadcrumbHandle,
            },
            {
              path: ":formId",
              element: <FormDetail />,
              loader: async ({ params }) =>
                await createGetFormDetailBreadcrumb(
                  queryClient,
                  params.formId!,
                  params.procedureId!,
                )(),
              handle: {
                breadcrumb: (match) => {
                  const data: {
                    label: string;
                    path?: string;
                  }[] = [
                    {
                      label: "Mis Trámites",
                      path: "/",
                    },
                    {
                      label: "Detalle",
                      path: `/tramite/${match.params.procedureId}`,
                    },
                    {
                      label: match?.data?.data?.name || "Nombre del Formulario",
                    },
                  ];
                  return data;
                },
              } as BreadcrumbHandle,
            },
            {
              path: "formulario-interno/:formId",
              element: <FormInterns />,
              loader: async ({ params }) =>
                await createGetFormDetailBreadcrumb(
                  queryClient,
                  params.formId!,
                  params.procedureId!,
                )(),
              handle: {
                breadcrumb: (match) => {
                  const data: {
                    label: string;
                    path?: string;
                  }[] = [
                    {
                      label: "Mis Trámites",
                      path: "/",
                    },
                    {
                      label: "Detalle",
                      path: `/tramite/${match.params.procedureId}`,
                    },
                    {
                      label: match?.data?.data?.name || "Nombre del Formulario",
                    },
                  ];
                  return data;
                },
              } as BreadcrumbHandle,
            },
          ],
          handle: {
            breadcrumb: () => [
              {
                label: "Trámites",
              },
            ],
          } as BreadcrumbHandle,
        },
        {
          path: "/expedientes",
          element: (
            <RoleGuard permissions={[Permissions.expedient_base_entities.read]}>
              <DigitalFilesPage />
            </RoleGuard>
          ),
          handle: {
            breadcrumb: () => [
              {
                label: "Expedientes",
              },
            ],
          } as BreadcrumbHandle,
        },
        {
          path: "/expedientes/entidad",
          element: (
            <RoleGuard
              permissions={[Permissions.expedient_base_entities.create]}
            >
              <EntityPage />
            </RoleGuard>
          ),
          handle: {
            breadcrumb: (match) => [
              {
                label: "Expedientes",
                path: `/expedientes${match.params.id ? `?entity=${match.params.id}` : ""}`,
              },
              {
                label: "Nueva Entidad",
              },
            ],
          } as BreadcrumbHandle,
        },
        {
          path: "/expedientes/entidad/:id?",
          element: (
            <RoleGuard
              permissions={[Permissions.expedient_base_entities.update]}
            >
              <EntityPage />
            </RoleGuard>
          ),
          loader: async ({ params }) =>
            await createEntityDetailsBreadcrumb(queryClient, params.id!)(),
          handle: {
            breadcrumb: (match) => [
              {
                label: "Expedientes",
                path: `/expedientes${match.params.id ? `?entity=${match.params.id}` : ""}`,
              },
              {
                label: match?.data?.data?.name ?? "Entidad",
              },
            ],
          } as BreadcrumbHandle,
        },
        {
          path: "/expedientes/entidad/:id/registro",
          element: (
            <RoleGuard
              permissions={[Permissions.expedient_base_registries.create]}
            >
              <EntityRegistryPage />
            </RoleGuard>
          ),
          handle: {
            breadcrumb: (match) => {
              return [
                {
                  label: "Expedientes",
                  path: `/expedientes${match.params.id ? `?entity=${match.params.id}` : ""}`,
                },
                {
                  label: "Nuevo Registro",
                },
              ];
            },
          } as BreadcrumbHandle,
        },
        {
          path: "/expedientes/entidad/:id/registro/:registryId?",
          element: (
            <RoleGuard
              permissions={[Permissions.expedient_base_registries.update]}
            >
              <EntityRegistryPage />
            </RoleGuard>
          ),
          loader: async ({ params }) =>
            await createRegistryDetailsBreadcrumb(
              queryClient,
              params.registryId!,
            )(),
          handle: {
            breadcrumb: (match) => {
              const [entityName, registryName] = match?.data?.data?.name?.split(
                "-separator-breadcrumb-",
              ) || ["", ""];
              return [
                {
                  label: "Expedientes",
                  path: `/expedientes${match.params.id ? `?entity=${match.params.id}` : ""}`,
                },
                ...(entityName
                  ? [
                      {
                        label: entityName,
                        path: `/expedientes/entidad/${match.params.id}/detalle-registro/${match.params.registryId}`,
                      },
                    ]
                  : []),
                {
                  label: registryName || "Registro",
                },
              ];
            },
          } as BreadcrumbHandle,
        },
        {
          path: "/expedientes/entidad/:id/detalle-registro/:registryId",
          element: (
            <RoleGuard
              permissions={[Permissions.expedient_base_registries.read]}
            >
              <RegistryDetailPage />
            </RoleGuard>
          ),
          loader: async ({ params }) =>
            await createRegistryDetailsBreadcrumb(
              queryClient,
              params.registryId!,
            )(),
          handle: {
            breadcrumb: (match) => {
              const [entityName, registryName] = match?.data?.data?.name?.split(
                "-separator-breadcrumb-",
              ) || ["", ""];
              return [
                {
                  label: "Expedientes",
                  path: `/expedientes${match.params.id ? `?entity=${match.params.id}` : ""}`,
                },
                ...(entityName
                  ? [
                      {
                        label: entityName,
                        path: `/expedientes?entity=${match.params.id}`,
                      },
                    ]
                  : []),
                {
                  label: registryName || "Detalle del Registro",
                },
              ];
            },
          } as BreadcrumbHandle,
        },
        {
          path: "/expedientes/entidad/:id/detalle-registro/:registryId/tramite/:procedureId",
          element: (
            <RoleGuard
              permissions={[
                Permissions.expedient_base_registries["read-formality"],
              ]}
            >
              <ProcedureDetailPage />
            </RoleGuard>
          ),
          children: [
            {
              index: true,
              element: <ProcedureFormsList />,
              loader: async ({ params }) =>
                await createFormalityRequestBreadcrumb(
                  queryClient,
                  params.procedureId!,
                )(),
              handle: {
                breadcrumb: (match) => {
                  const data: {
                    label: string;
                    path?: string;
                  }[] = [
                    {
                      label: "Registro",
                      path: `/expedientes/entidad/${match?.params?.id}/detalle-registro/${match?.params?.registryId}`,
                    },
                    {
                      label: match?.data?.data?.name || "Detalle",
                    },
                  ];
                  return data;
                },
              } as BreadcrumbHandle,
            },
            {
              path: "correccion/:formId",
              element: (
                <RoleGuard
                  permissions={[
                    Permissions.widgets.submissions,
                    Permissions.expedient_base_registries["read-formality"],
                  ]}
                  canHaveSomePermissions
                >
                  <FormCorrection />
                </RoleGuard>
              ),
              loader: async ({ params }) =>
                await createGetFormDetailBreadcrumb(
                  queryClient,
                  params.formId!,
                  params.procedureId!,
                )(),
              handle: {
                breadcrumb: (match) => {
                  const data: {
                    label: string;
                    path?: string;
                  }[] = [
                    {
                      label: "Registro",
                      path: `/expedientes/entidad/${match?.params?.id}/detalle-registro/${match?.params?.registryId}`,
                    },
                    {
                      label: "Detalle",
                      path: `/expedientes/entidad/${match?.params?.id}/detalle-registro/${match?.params?.registryId}/tramite/${match.params.procedureId}`,
                    },
                    {
                      label: match?.data?.data?.name || "Nombre del Formulario",
                    },
                  ];
                  return data;
                },
              } as BreadcrumbHandle,
            },
            {
              path: "firma/:singRequestId",
              element: (
                <RoleGuard
                  permissions={[
                    Permissions.widgets.submissions,
                    Permissions.expedient_base_registries["read-formality"],
                  ]}
                  canHaveSomePermissions
                >
                  <SignatureDetail />
                </RoleGuard>
              ),
              handle: {
                breadcrumb: (match) => {
                  const data: { label: string; path?: string }[] = [
                    {
                      label: "Registro",
                      path: `/expedientes/entidad/${match?.params?.id}/detalle-registro/${match?.params?.registryId}`,
                    },
                    {
                      label: "Detalle",
                      path: `/expedientes/entidad/${match?.params?.id}/detalle-registro/${match?.params?.registryId}/tramite/${match.params.procedureId}`,
                    },
                    { label: "Firma de documentos" },
                  ];
                  return data;
                },
              } as BreadcrumbHandle,
            },
            {
              path: "pago/:paymentRequestId",
              element: (
                <RoleGuard
                  permissions={[
                    Permissions.widgets.submissions,
                    Permissions.expedient_base_registries["read-formality"],
                  ]}
                  canHaveSomePermissions
                >
                  <PaymentDetail />
                </RoleGuard>
              ),
              handle: {
                breadcrumb: (match) => {
                  const data: { label: string; path?: string }[] = [
                    {
                      label: "Registro",
                      path: `/expedientes/entidad/${match?.params?.id}/detalle-registro/${match?.params?.registryId}`,
                    },
                    {
                      label: "Detalle",
                      path: `/expedientes/entidad/${match?.params?.id}/detalle-registro/${match?.params?.registryId}/tramite/${match.params.procedureId}`,
                    },
                    { label: "Detalle de pago" },
                  ];
                  return data;
                },
              } as BreadcrumbHandle,
            },
            {
              path: ":formId",
              element: <FormDetail />,
              loader: async ({ params }) =>
                await createGetFormDetailBreadcrumb(
                  queryClient,
                  params.formId!,
                  params.procedureId!,
                )(),
              handle: {
                breadcrumb: (match) => {
                  const data: {
                    label: string;
                    path?: string;
                  }[] = [
                    {
                      label: "Registro",
                      path: `/expedientes/entidad/${match?.params?.id}/detalle-registro/${match?.params?.registryId}`,
                    },
                    {
                      label: "Detalle",
                      path: `/expedientes/entidad/${match?.params?.id}/detalle-registro/${match?.params?.registryId}/tramite/${match.params.procedureId}`,
                    },
                    {
                      label: match?.data?.data?.name || "Nombre del Formulario",
                    },
                  ];
                  return data;
                },
              } as BreadcrumbHandle,
            },
            {
              path: "formulario-interno/:formId",
              element: <FormInterns />,
              loader: async ({ params }) =>
                await createGetFormDetailBreadcrumb(
                  queryClient,
                  params.formId!,
                  params.procedureId!,
                )(),
              handle: {
                breadcrumb: (match) => {
                  const data: {
                    label: string;
                    path?: string;
                  }[] = [
                    {
                      label: "Registro",
                      path: `/expedientes/entidad/${match?.params?.id}/detalle-registro/${match?.params?.registryId}`,
                    },
                    {
                      label: "Detalle",
                      path: `/expedientes/entidad/${match?.params?.id}/detalle-registro/${match?.params?.registryId}/tramite/${match.params.procedureId}`,
                    },
                    {
                      label: match?.data?.data?.name || "Nombre del Formulario",
                    },
                  ];
                  return data;
                },
              } as BreadcrumbHandle,
            },
          ],
          handle: {
            breadcrumb: (match) => {
              const [entityName, registryName] = match?.data?.data?.name?.split(
                "-separator-breadcrumb-",
              ) || ["", ""];
              return [
                {
                  label: "Expedientes",
                  path: `/expedientes${match.params.id ? `?entity=${match.params.id}` : ""}`,
                },
                ...(entityName
                  ? [
                      {
                        label: entityName,
                        path: `/expedientes?entity=${match.params.id}`,
                      },
                    ]
                  : []),
                {
                  label: registryName || "Detalle del Registro",
                },
                {
                  label: "Detalle de solicitud",
                },
              ];
            },
          } as BreadcrumbHandle,
        },
        {
          path: "/403",
          element: <Page403 />,
          handle: {
            breadcrumb: () => [
              {
                label: "Acceso no autorizado",
              },
            ],
          } as BreadcrumbHandle,
        },
        ...(shouldShowInfoPage
          ? [
              {
                path: "-/info",
                Component: () => <InfoPage />,
                handle: {
                  breadcrumb: () => [
                    {
                      label: "Info",
                    },
                  ],
                } as BreadcrumbHandle,
              },
            ]
          : []),
        {
          path: "*",
          loader: () => {
            return redirect("/");
          },
        },
      ],
    },
  ]);
};

export default createAppRouter;
